import { AssistantIcon, ContactIcon, DashboardIcon, ReportIcon, SetupIcon } from '@olytic/foundation';
import { type AdminSection, AdminOverview } from '@olytic/foundation/admin';
import { Copy, Handshake, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { OBJECTS, SETUP_ROUTES, setupPagesFor } from '../navigation';

/**
 * The cards on the Setup home page (SetupPages.tsx), each opening its page with
 * `open(href)`, for someone with this role key: a card whose page is Admin-only
 * (`adminOnly` in SETUP_PAGES) is left out for anyone else. Kept apart from
 * SetupPages.tsx, which loads the auth client, so the cards can be tested.
 */
export function setupHomeSections(open: (href: string) => void, role: string | undefined): AdminSection[] {
  const offered = new Set(setupPagesFor(role).map((page) => page.href));
  const sections: (Omit<AdminSection, 'onOpen'> & { href: string })[] = [
    {
      key: 'users',
      title: 'Users',
      description: 'Who can use Mothership, and with which role.',
      icon: <ContactIcon />,
      href: SETUP_ROUTES.users,
    },
    {
      key: 'ai-usage',
      title: 'AI usage',
      description: 'How much the assistant is used, by person and capability.',
      icon: <AssistantIcon />,
      href: SETUP_ROUTES.aiUsage,
    },
    {
      key: 'ai-credits',
      title: 'Credits & limits',
      description: 'AI tokens used this month, and a monthly cap that warns.',
      icon: <ReportIcon />,
      href: SETUP_ROUTES.aiCredits,
    },
    {
      key: 'ai-capabilities',
      title: 'Capabilities & models',
      description: 'What the assistant may do, and which models it uses.',
      icon: <SetupIcon />,
      href: SETUP_ROUTES.aiCapabilities,
    },
    {
      key: 'objects',
      title: 'Objects',
      description: 'The ops tables and their fields.',
      summary: `${OBJECTS.length} objects`,
      icon: <DashboardIcon />,
      href: SETUP_ROUTES.objects,
    },
    {
      key: 'recycle-bin',
      title: 'Recycle Bin',
      description: 'Deleted records, to restore or delete for good.',
      icon: <Trash2 />,
      href: SETUP_ROUTES.recycleBin,
    },
    {
      key: 'possible-duplicates',
      title: 'Possible Duplicates',
      description: 'Accounts and contacts that share a name, to review and merge.',
      icon: <Copy />,
      href: SETUP_ROUTES.possibleDuplicates,
    },
    {
      key: 'partners',
      title: 'Partners',
      description: 'Partner applications: who applied, and where each stands.',
      icon: <Handshake />,
      href: SETUP_ROUTES.partners,
    },
  ];
  return sections.filter((s) => offered.has(s.href)).map((s) => ({ ...s, onOpen: () => open(s.href) }));
}

/** The Setup home's cards for someone with this role key, each opening its page. */
export function SetupHomeOverview({ role }: { role: string | undefined }): JSX.Element {
  const navigate = useNavigate();
  return <AdminOverview sections={setupHomeSections(navigate, role)} />;
}
